import {TicketSaleStatus} from '@wix/events-types'
import {loadMadeFor} from '@wix/panda-js-utils'
import {MAX_ZOOM, OnPlaceMouseEvent, PlanScrollContainer, Plan as PlanViewer, useViewBox} from '@wix/seating-viewer'
import {MODAL_BUTTONS} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useCurrencyFormatter} from '../../../../../../../commons/hooks/currency'
import {isPlaceReservableAsWhole} from '../../../../selectors/seating/place'
import {calculatePlacesStock, getAnyPlaceByPlanPlaceId} from '../../../../selectors/seating/places'
import {PopoverData, SeatingPopover} from '../seating-popover'
import {Controls} from './controls'
import {PlanProps} from './interfaces'
import {Legend} from './legend'
import s from './plan.scss'

const ZOOM_STEP = 0.2

export const Plan = ({
  plan,
  placesInBasket,
  onPlaceClick,
  placesWithTicketInfo,
  selectedPlace,
  showAccessibilityMode,
  forcedPopover,
  setAccessibilityToast,
  seatingMapButtonClick,
}: PlanProps) => {
  const {isMobile} = useEnvironment()
  const {t} = useTranslation()
  const container = React.useRef<HTMLDivElement>(null)
  const svgContainer = React.useRef<HTMLDivElement>(null)
  const {getFormattedMoney, getTicketPriceText} = useCurrencyFormatter()

  const [fontsReady, setFontsReady] = useState(false)

  const viewBoxInfo = useViewBox(plan, isMobile, svgContainer)
  const {ready, viewBox, zoom, changeSvgViewBox, minZoom} = viewBoxInfo

  const [popoverData, setPopoverData] = useState(null as PopoverData)
  const placesStock = useMemo(() => calculatePlacesStock(placesWithTicketInfo), [placesWithTicketInfo])

  useEffect(() => {
    loadMadeFor().then(() => {
      setFontsReady(true)
    })
  }, [setFontsReady])

  useEffect(() => {
    if (forcedPopover) {
      const info = getAnyPlaceByPlanPlaceId(placesWithTicketInfo, forcedPopover)
      const element = document.getElementById(forcedPopover)
      const boundingClientRect = isPlaceReservableAsWhole(info)
        ? element?.parentElement?.parentElement?.getBoundingClientRect()
        : element?.getBoundingClientRect()
      const containerBoundingClientRect = container.current.getBoundingClientRect()

      if (!info || !boundingClientRect) {
        return undefined
      }

      setPopoverData({boundingClientRect, info, containerBoundingClientRect} as PopoverData)
    } else {
      setPopoverData(null)
    }
  }, [forcedPopover])

  useEffect(() => {
    if (popoverData) {
      setPopoverData(null)
    }
  }, [viewBox])

  const onPlaceMouseEnter: OnPlaceMouseEvent = useCallback(
    ({place, event}) => {
      const info = getAnyPlaceByPlanPlaceId(placesWithTicketInfo, place.id)
      const ticketSaleStatus = info?.ticket?.saleStatus ?? TicketSaleStatus.SALE_ENDED

      if (ticketSaleStatus === TicketSaleStatus.SALE_ENDED) {
        return null
      }

      const containerBoundingClientRect = container.current.getBoundingClientRect()
      const boundingClientRect = getBoundingRectangle(event.currentTarget)

      setPopoverData({boundingClientRect, info, containerBoundingClientRect} as PopoverData)
    },
    [placesWithTicketInfo],
  )

  const onPlaceMouseLeave: OnPlaceMouseEvent = useCallback(() => {
    setPopoverData(null)
  }, [])

  const selectedPlacesIds = useMemo(() => placesInBasket.map(place => place.id), [placesInBasket])

  const handleZoomIn = () => {
    seatingMapButtonClick(MODAL_BUTTONS.ZOOM_IN)
    changeSvgViewBox({deltaScale: ZOOM_STEP})
  }

  const handleZoomOut = () => {
    seatingMapButtonClick(MODAL_BUTTONS.ZOOM_OUT)
    changeSvgViewBox({deltaScale: -ZOOM_STEP})
  }

  return (
    <PlanScrollContainer
      ref={container}
      viewBoxInfo={viewBoxInfo}
      className={classNames({[s.mobileContainer]: isMobile})}
    >
      {showAccessibilityMode ? (
        <div
          className={s.overlay}
          data-hook={DH.ACCESSIBILITY_MAP_OVERLAY}
          onClick={() => setAccessibilityToast(true)}
        />
      ) : null}
      <div aria-hidden="true" ref={svgContainer} className={s.svgContainer}>
        {ready && fontsReady ? (
          <PlanViewer
            onPlaceMouseLeave={isMobile || showAccessibilityMode ? null : onPlaceMouseLeave}
            onPlaceMouseEnter={isMobile || showAccessibilityMode ? null : onPlaceMouseEnter}
            placesStock={placesStock}
            currentlyClickedPlaceId={selectedPlace?.id}
            selectedPlaceIds={selectedPlacesIds}
            plan={plan}
            svgViewBox={viewBox}
            onPlaceClick={showAccessibilityMode ? undefined : onPlaceClick}
            mobile={isMobile}
          />
        ) : null}
      </div>
      {isMobile ? null : <Legend t={t} getFormattedMoney={getFormattedMoney} getTicketPriceText={getTicketPriceText} />}
      <Controls
        zoomOutDisabled={zoom === minZoom}
        zoomInDisabled={zoom === MAX_ZOOM}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
      />
      {popoverData ? <SeatingPopover popoverData={popoverData} /> : null}
    </PlanScrollContainer>
  )
}

// Work-around for FireFox bug of not calculating SVG element sizes properly
// Basically some elements are painted too large in FireFox
// This mainly happens with <g> in our case so when we encounter <g>, we instead
// calculate what the overall bounding rectangle would be of inner shapes.
const getBoundingRectangle = (target: Element): DOMRect => {
  const targets: Element[] = []

  const measuredTagNames = ['rect', 'ellipse']

  if (target.tagName.toLowerCase() === 'g') {
    const targetSelection = target.querySelectorAll(measuredTagNames.join(','))
    targetSelection.forEach(item => targets.push(item))
  } else {
    targets.push(target)
  }

  const boundingRectangles = targets.map(item => item.getBoundingClientRect())

  const bottoms = boundingRectangles.map(({bottom}) => bottom)
  const tops = boundingRectangles.map(({top}) => top)
  const lefts = boundingRectangles.map(({left}) => left)
  const rights = boundingRectangles.map(({right}) => right)

  const bottom = Math.max(...bottoms)
  const top = Math.min(...tops)
  const right = Math.max(...rights)
  const left = Math.min(...lefts)

  const rect = {
    bottom,
    top,
    left,
    right,
    height: bottom - top,
    width: right - left,
    x: left,
    y: top,
  }

  return {...rect, toJSON: () => JSON.stringify(rect)}
}
